import { useMemo, useState } from 'react';
import { Archive, Eye, RotateCcw } from 'lucide-react';

import { LocalTime } from '~/components/common/LocalTime';
import { aiStatus } from '~/components/pages/ai/common/statusMaps';
import {
  DataTable,
  DropdownMenu,
  type DataTableColumn,
  StatusChip,
  LoadError,
} from '~/components/ui';
import { apiErrorMessage } from '~/lib/apiError';
import { useListDigitalAssistantVersionsQuery } from '~/modules/query/ai/digitalAssistants';
import type { DigitalAssistantVersion } from '~/modules/query/ai/types';

export interface AssistantVersionHistoryProps {
  assistantId: string;
  latestVersion: number;
  onPreview: (version: DigitalAssistantVersion) => void;
  onRestore: (version: DigitalAssistantVersion) => void;
  onArchive: (version: DigitalAssistantVersion) => void;
}

type VersionSortField = 'version' | 'createdBy' | 'createdAt';

const authorLabel = (version: DigitalAssistantVersion): string =>
  version.createdByUser?.displayName || version.createdByUser?.email || version.createdBy;

function compareVersions(
  left: DigitalAssistantVersion,
  right: DigitalAssistantVersion,
  field: VersionSortField,
): number {
  switch (field) {
    case 'version':
      return left.versionNumber - right.versionNumber;
    case 'createdBy':
      return authorLabel(left).localeCompare(authorLabel(right));
    case 'createdAt':
      return new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime();
  }
}

/** The saved versions of one assistant, with the restore/archive actions the page confirms. */
export default function AssistantVersionHistory({
  assistantId,
  latestVersion,
  onPreview,
  onRestore,
  onArchive,
}: AssistantVersionHistoryProps) {
  const { data, error, isLoading, isFetching, refetch } =
    useListDigitalAssistantVersionsQuery(assistantId);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const [sort, setSort] = useState<{ field: VersionSortField; order: 'asc' | 'desc' } | null>(null);

  const versions = useMemo(() => {
    const rows = data?.data ?? [];
    if (!sort) {
      return rows;
    }
    const direction = sort.order === 'asc' ? 1 : -1;
    return [...rows].sort((left, right) => direction * compareVersions(left, right, sort.field));
  }, [data, sort]);

  const onSort = (field: string) => {
    const next = field as VersionSortField;
    setSort((current) =>
      current?.field === next
        ? { field: next, order: current.order === 'asc' ? 'desc' : 'asc' }
        : { field: next, order: 'asc' },
    );
  };

  if (error && !data) {
    return (
      <LoadError
        what="the version history"
        message={apiErrorMessage(error)}
        onRetry={() => refetch()}
        testId="ai-digital-assistants-versions"
      />
    );
  }

  const columns: DataTableColumn<DigitalAssistantVersion>[] = [
    {
      id: 'version',
      header: 'Version',
      sortable: true,
      cell: (version) => (
        <span className="font-medium text-gray-800 dark:text-white/90">
          #{version.versionNumber}
          {version.versionNumber === latestVersion ? (
            <span className="ml-1.5 text-xs font-normal text-gray-500 dark:text-gray-400">
              (latest)
            </span>
          ) : null}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (version) => <StatusChip {...aiStatus(version.status)} />,
    },
    {
      id: 'createdBy',
      header: 'Created by',
      sortable: true,
      cell: authorLabel,
    },
    {
      id: 'createdAt',
      header: 'Created',
      sortable: true,
      cell: (version) => <LocalTime value={version.createdAt} />,
    },
    {
      id: 'changeNote',
      header: 'Change note',
      wrap: true,
      cell: (version) =>
        version.changeNote || <span className="text-gray-400 dark:text-gray-500">—</span>,
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (version) => (
        <div className="flex w-full items-center justify-center">
          <DropdownMenu
            ariaLabel={`Open actions for version ${version.versionNumber}`}
            testId={`ai-digital-assistants-version-actions-${version.versionNumber}`}
            isOpen={openMenuId === version.id}
            onOpenChange={(open) => setOpenMenuId(open ? version.id : null)}
            items={[
              {
                label: 'Preview',
                testId: 'preview',
                icon: <Eye className="size-4" aria-hidden="true" />,
                onClick: () => onPreview(version),
              },
              {
                label: 'Restore',
                testId: 'restore',
                icon: <RotateCcw className="size-4" aria-hidden="true" />,
                onClick: () => onRestore(version),
              },
              { divider: true },
              {
                label: 'Archive',
                testId: 'archive',
                icon: <Archive className="size-4" aria-hidden="true" />,
                destructive: true,
                disabled: version.versionNumber === latestVersion,
                onClick: () => onArchive(version),
              },
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <DataTable<DigitalAssistantVersion>
      testId="ai-digital-assistants-versions-table"
      columns={columns}
      data={versions}
      rowKey={(version) => version.id}
      sort={sort?.field}
      order={sort?.order ?? 'asc'}
      onSort={onSort}
      loading={isLoading}
      refreshing={isFetching && Boolean(data)}
      emptyMessage="No versions yet"
      emptyDescription="Saving the assistant creates its first version. Restore and archive act on the versions listed here."
    />
  );
}
